import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { TouchableOpacity, View, Text, Alert, StyleSheet, TextInput, Image } from 'react-native';
import { FontAwesome5 } from '@expo/vector-icons';
import { BasicLayout, CommonContainer } from '../../../common/components/CustomLayout';
import { BOHTBody, BOHTD, BOHTDIconBox, BOHTH2, BOHTHead, BOHTR, BOHTable } from '../../../common/components/bohtable';
import { BOHButton, BOHToolbar } from '../../../common/components/bohtoolbar';
import { TextMediumSize } from '../../../common/constants/Fonts';
import AddKeyModal from './modals/AddKeyModal';
import { getAPICall, postAPICall } from '../../../api/BaseAPI';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL, BASE_URL } from '../../../common/constants/AppConstants';

const CONTENT_TYPE_BADGES = {
  text: { label: 'Text', bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
  html: { label: 'HTML', bg: '#fff3e0', color: '#e65100', border: '#ffcc80' },
  image: { label: 'Image', bg: '#e3f2fd', color: '#1565c0', border: '#90caf9' },
};

const isDevMode = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const ManageKeys = ({ navigation, goBack, initialBrandId = '' }) => {
  const [tableData, setTableData] = useState([]);
  const [brands, setBrands] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddModalVisible, setAddModalVisible] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterBrand, setFilterBrand] = useState(initialBrandId);
  const [filterContentType, setFilterContentType] = useState('');

  const InitialWidths = [160, 140, 100, 180, 280, 200, 60, 60];

  useEffect(() => {
    if (initialBrandId) {
      setFilterBrand(initialBrandId);
    }
  }, [initialBrandId]);

  useEffect(() => {
    fetchKeys();
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    await getAPICall('price/getbrandsdata', (jsonRes, status) => {
      if (status === 200) {
        setBrands(Array.isArray(jsonRes) ? jsonRes : jsonRes.data || []);
      }
    });
  };

  const fetchKeys = async () => {
    setIsLoading(true);
    const brandParam = filterBrand ? `?brand_id=${filterBrand}` : '';
    await getAPICall(`managekeys/${brandParam}`, (jsonRes, status, error) => {
      setIsLoading(false);
      if (status === 200 && jsonRes?.success) {
        setTableData(jsonRes.data || []);
      } else {
        Alert.alert('Error', error || 'Failed to fetch keys data');
      }
    });
  };

  const openAddModal = () => {
    setSelectedKey(null);
    setAddModalVisible(true);
  };

  const defaultBrandId = filterBrand;

  const populateKeys = async () => {
    if (!filterBrand) {
      if (typeof window !== 'undefined') window.alert('Please select a brand from the filter before populating keys.');
      else Alert.alert('Select a Brand', 'Please select a brand from the filter before populating keys.');
      return;
    }
    const confirmed = typeof window !== 'undefined'
      ? window.confirm('This will create default CMS keys for the selected brand. Continue?')
      : true;
    if (!confirmed) return;
    setIsLoading(true);
    await postAPICall('managekeys/populate', { brand_id: Number(filterBrand) }, (jsonRes, status, error) => {
      setIsLoading(false);
      if (status === 200) {
        if (typeof window !== 'undefined') window.alert(jsonRes?.message || 'Keys populated successfully.');
        else Alert.alert('Success', jsonRes?.message || 'Keys populated successfully.');
        fetchKeys();
      } else {
        if (typeof window !== 'undefined') window.alert(error || jsonRes?.message || 'Failed to populate keys.');
        else Alert.alert('Error', error || jsonRes?.message || 'Failed to populate keys.');
      }
    });
  };

  const editKey = (item) => {
    setSelectedKey(item);
    setAddModalVisible(true);
  };

  const deleteKey = async (id) => {
    Alert.alert(
      'Confirm Delete',
      'Are you sure you want to delete this key?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            setIsLoading(true);
            await postAPICall('managekeys/delete', { id }, (jsonRes, status, error) => {
              setIsLoading(false);
              if (status === 200 && jsonRes?.success) {
                fetchKeys();
              } else {
                Alert.alert('Error', jsonRes?.message || error || 'Failed to delete key');
              }
            });
          }
        }
      ]
    );
  };

  const saveKey = useCallback(async (formData: FormData) => {
    setIsLoading(true);
    try {
      const token = await AsyncStorage.getItem('access-token');
      const isEdit = formData.get('id');
      const endpoint = isEdit ? 'managekeys/update' : 'managekeys/defaults/create';
      const response = await fetch(`${API_URL}/${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      const jsonRes = await response.json();
      setIsLoading(false);
      if (jsonRes?.success) {
        setAddModalVisible(false);
        fetchKeys();
      } else {
        Alert.alert('Error', jsonRes?.message || 'Failed to save key');
      }
    } catch (e) {
      setIsLoading(false);
      Alert.alert('Error', 'Failed to save key');
    }
  }, []);

  const closeAddModal = useCallback(() => setAddModalVisible(false), []);

  const uniqueLocations = useMemo(() => {
    const locations = [...new Set(tableData.map(item => item.location))];
    return locations.sort();
  }, [tableData]);

  const getBrandName = (brand_id: number) => {
    const brand = brands.find(b => b.id === brand_id);
    return brand?.brand || `Brand ${brand_id}`;
  };

  const filteredData = useMemo(() => {
    return tableData.filter(item => {
      const searchMatch = !searchText ||
        item.key_name?.toLowerCase().includes(searchText.toLowerCase()) ||
        item.key_value?.toLowerCase().includes(searchText.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchText.toLowerCase()) ||
        item.location?.toLowerCase().includes(searchText.toLowerCase()) ||
        getBrandName(item.brand_id)?.toLowerCase().includes(searchText.toLowerCase());
      const locationMatch = !filterLocation || item.location === filterLocation;
      const brandMatch = !filterBrand || String(item.brand_id) === filterBrand;
      const typeMatch = !filterContentType || item.content_type === filterContentType;
      return searchMatch && locationMatch && brandMatch && typeMatch;
    });
  }, [tableData, searchText, filterLocation, filterBrand, filterContentType]);

  const renderTableData = useMemo(() => {
    return (
      <>
        {filteredData.map((item, index) => {
          const badge = CONTENT_TYPE_BADGES[item.content_type] || CONTENT_TYPE_BADGES.text;
          return (
            <BOHTR key={item.id || index}>
              <BOHTD width={InitialWidths[1]}>
                <Text style={{ fontSize: 12 }}>{getBrandName(item.brand_id)}</Text>
              </BOHTD>
              <BOHTD width={InitialWidths[2]}>
                <View style={{ backgroundColor: badge.bg, borderWidth: 1, borderColor: badge.border, borderRadius: 12, paddingHorizontal: 8, paddingVertical: 2, alignSelf: 'flex-start' }}>
                  <Text style={{ fontSize: 11, color: badge.color, fontWeight: '600' }}>{badge.label}</Text>
                </View>
              </BOHTD>
              <BOHTD width={InitialWidths[3]}>{item.location}</BOHTD>
              <BOHTD width={InitialWidths[4]}>
                {item.content_type === 'image' ? (
                  <View style={{ gap: 4 }}>
                    {item.image_url ? (
                      <TouchableOpacity onPress={() => setLightboxUrl(item.image_url.startsWith('http') ? item.image_url : `${API_URL}${item.image_url}`)}>
                        <View style={{ position: 'relative' }}>
                          <Image
                            source={{ uri: item.image_url.startsWith('http') ? item.image_url : `${API_URL}${item.image_url}` }}
                            style={{ width: 60, height: 40, borderRadius: 4 }}
                            resizeMode="cover"
                          />
                          <View style={{ position: 'absolute', top: 2, right: 2, backgroundColor: 'rgba(0,0,0,0.45)', borderRadius: 3, padding: 2 }}>
                            <FontAwesome5 name="expand" size={8} color="#fff" />
                          </View>
                        </View>
                      </TouchableOpacity>
                    ) : <Text style={{ color: '#999', fontSize: 12 }}>No image</Text>}
                    {item.key_value ? (
                      <Text style={{ fontSize: 10, color: '#888', fontStyle: 'italic' }} numberOfLines={2}>{item.key_value}</Text>
                    ) : null}
                  </View>
                ) : item.content_type === 'html' ? (
                  <div
                    style={{ fontSize: 12, maxWidth: 260, overflow: 'hidden', maxHeight: 48, lineHeight: '1.4' }}
                    dangerouslySetInnerHTML={{ __html: item.key_value || '' }}
                  />
                ) : (
                  <Text style={{ fontSize: 12 }} numberOfLines={2}>{item.key_value || '-'}</Text>
                )}
              </BOHTD>
              <BOHTD width={InitialWidths[5]}>
                <Text style={{ fontSize: 12, color: '#444', flexWrap: 'wrap' }} numberOfLines={3}>{item.description || '-'}</Text>
              </BOHTD>
              <BOHTD width={InitialWidths[0]}>
                <Text style={{ fontSize: 12, color: '#555', fontFamily: 'monospace' }} numberOfLines={2}>{item.key_name}</Text>
              </BOHTD>
              <BOHTDIconBox width={InitialWidths[6]}>
                <TouchableOpacity onPress={() => editKey(item)}>
                  <FontAwesome5 size={TextMediumSize} name="edit" color="black" />
                </TouchableOpacity>
              </BOHTDIconBox>
              <BOHTDIconBox width={InitialWidths[7]}>
                <TouchableOpacity onPress={() => deleteKey(item.id)}>
                  <FontAwesome5 size={TextMediumSize} name="times" color="red" />
                </TouchableOpacity>
              </BOHTDIconBox>
            </BOHTR>
          );
        })}
      </>
    );
  }, [filteredData, brands]);

  const tableElement = useMemo(() => (
    <BOHTable isLoading={isLoading}>
      <BOHTHead>
        <BOHTR>
          <BOHTH2 width={InitialWidths[1]}>Brand</BOHTH2>
          <BOHTH2 width={InitialWidths[2]}>Type</BOHTH2>
          <BOHTH2 width={InitialWidths[3]}>Page</BOHTH2>
          <BOHTH2 width={InitialWidths[4]}>Text / Image</BOHTH2>
          <BOHTH2 width={InitialWidths[5]}>Description</BOHTH2>
          <BOHTH2 width={InitialWidths[0]}>Key Name</BOHTH2>
          <BOHTH2 width={InitialWidths[6]}>Edit</BOHTH2>
          <BOHTH2 width={InitialWidths[7]}>Del</BOHTH2>
        </BOHTR>
      </BOHTHead>
      <BOHTBody>{renderTableData}</BOHTBody>
    </BOHTable>
  ), [isLoading, filteredData, brands]);

  return (
    <BasicLayout navigation={navigation} goBack={goBack} screenName={'CMS Settings'}>
      <CommonContainer>
        <BOHToolbar>
          <View style={styles.searchContainer}>
            <FontAwesome5 name="search" size={16} color="#666" style={styles.searchIcon} />
            <TextInput
              style={styles.searchInput}
              value={searchText}
              onChangeText={setSearchText}
              placeholder="Search keys, values, or descriptions..."
              placeholderTextColor="#999"
            />
            {searchText ? (
              <TouchableOpacity onPress={() => setSearchText('')} style={styles.clearIcon}>
                <FontAwesome5 name="times-circle" size={16} color="#999" />
              </TouchableOpacity>
            ) : null}
          </View>
          <View style={styles.filterContainer}>
            <Text style={styles.filterLabel}>Brand:</Text>
            <select
              value={filterBrand}
              onChange={(e) => setFilterBrand(e.target.value)}
              style={styles.filterSelect}
            >
              <option value="">All Brands</option>
              {brands.map((b: any) => (
                <option key={b.id} value={String(b.id)}>{b.brand}</option>
              ))}
            </select>
          </View>
          <View style={styles.filterContainer}>
            <Text style={styles.filterLabel}>Type:</Text>
            <select
              value={filterContentType}
              onChange={(e) => setFilterContentType(e.target.value)}
              style={styles.filterSelect}
            >
              <option value="">All Types</option>
              <option value="text">Text</option>
              <option value="html">HTML</option>
              <option value="image">Image</option>
            </select>
          </View>
          <View style={styles.filterContainer}>
            <Text style={styles.filterLabel}>Location:</Text>
            <select
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
              style={styles.filterSelect}
            >
              <option value="">All Locations</option>
              {uniqueLocations.map((location) => (
                <option key={location} value={location}>{location}</option>
              ))}
            </select>
          </View>
          <View style={{ flexDirection: 'row', gap: 8, marginLeft: 'auto' as any }}>
            <BOHButton label="Add Key" onPress={openAddModal} />
            <TouchableOpacity
              onPress={populateKeys}
              style={[
                styles.populateBtn,
                !filterBrand && styles.populateBtnDisabled,
              ]}
            >
              <FontAwesome5 name="magic" size={13} color={filterBrand ? '#fff' : '#aaa'} style={{ marginRight: 6 }} />
              <Text style={[styles.populateBtnText, !filterBrand && styles.populateBtnTextDisabled]}>Populate Keys</Text>
            </TouchableOpacity>
          </View>
        </BOHToolbar>
        
        <View style={styles.statsContainer}>
          <Text style={styles.statsText}>
            Showing {filteredData.length} of {tableData.length} keys
          </Text>
        </View>

        {tableElement}
        
        {isAddModalVisible && (
          <AddKeyModal
            isModalVisible={true}
            details={selectedKey}
            closeModal={closeAddModal}
            brands={brands}
            onSave={saveKey}
            defaultBrandId={selectedKey ? undefined : defaultBrandId}
          />
        )}
      </CommonContainer>

      {/* Lightbox */}
      {lightboxUrl && (
        <TouchableOpacity
          style={styles.lightboxBackdrop}
          activeOpacity={1}
          onPress={() => setLightboxUrl(null)}
        >
          <View style={styles.lightboxContainer}>
            <TouchableOpacity style={styles.lightboxClose} onPress={() => setLightboxUrl(null)}>
              <FontAwesome5 name="times" size={18} color="#fff" />
            </TouchableOpacity>
            <Image
              source={{ uri: lightboxUrl }}
              style={styles.lightboxImage}
              resizeMode="contain"
            />
          </View>
        </TouchableOpacity>
      )}
    </BasicLayout>
  );
};

const styles = StyleSheet.create({
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    paddingHorizontal: 12,
    marginLeft: 15,
    flex: 1,
    minWidth: 320,
    height: 40,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    outlineStyle: 'none',
    border: 'none',
    backgroundColor: 'transparent',
  },
  clearIcon: {
    padding: 4,
  },
  filterContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 15,
    gap: 8,
  },
  filterLabel: {
    fontSize: 14,
    fontWeight: '600',
  },
  filterSelect: {
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 4,
    padding: 8,
    fontSize: 14,
    minWidth: 180,
    height: 40,
  },
  clearFilterButton: {
    backgroundColor: '#f44336',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 4,
  },
  clearFilterText: {
    color: 'white',
    fontSize: 12,
    fontWeight: '600',
  },
  statsContainer: {
    paddingVertical: 8,
  },
  statsText: {
    fontSize: 13,
    color: '#666',
    fontStyle: 'italic',
  },
  populateBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#7c3aed',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    marginLeft: 8,
  },
  populateBtnDisabled: {
    backgroundColor: '#e5e7eb',
  },
  populateBtnText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
  populateBtnTextDisabled: {
    color: '#aaa',
  },
  lightboxBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.85)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
  },
  lightboxContainer: {
    width: '80%',
    maxWidth: 800,
    aspectRatio: 16 / 9,
    backgroundColor: '#000',
    borderRadius: 8,
    overflow: 'hidden',
    position: 'relative',
  },
  lightboxImage: {
    width: '100%',
    height: '100%',
  },
  lightboxClose: {
    position: 'absolute',
    top: 12,
    right: 12,
    backgroundColor: 'rgba(0,0,0,0.6)',
    borderRadius: 20,
    width: 36,
    height: 36,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
  },
});

export default ManageKeys;
